import clsx from 'clsx'
import { pascalCase } from 'pascal-case'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Button as MuiButton,
  CircularProgress,
  makeStyles,
} from '@material-ui/core'

const extendedColors = [
  'lightPurple',
  'purple',
  'orange',
  'yellow',
  'darkGreen',
  'lightGreen',
  'white',
  'darkRed',
  'red',
]

const useStyles = makeStyles(theme => ({
  // https://github.com/mui-org/material-ui/blob/fad48de8f7d9a449adb4ab9796e9ed1c2c0593ca/packages/material-ui/src/Button/Button.js#L131
  ...extendedColors.reduce((colorStyles, colorName) => {
    colorStyles[`contained${pascalCase(colorName)}`] = {
      color: theme.palette[colorName].contrastText,
      backgroundColor: theme.palette[colorName].main,
      '&:hover': {
        backgroundColor: theme.palette[colorName].dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette[colorName].main,
        },
      },
    }
    colorStyles[`outlined${pascalCase(colorName)}`] = {
      borderColor: theme.palette[colorName].main,
      color: theme.palette[colorName].main,
    }
    return colorStyles
  }, {}),
  buttonProgress: {
    color: theme.palette.secondary.main,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  root: {
    textTransform: 'none',
  },
  wrapper: {
    display: 'inline-flex',
    position: 'relative',
  },
}))

export const Button = ({
  children,
  className,
  color,
  isLoading,
  variant,
  ...props
}) => {
  const classes = useStyles()
  const extendedColor = extendedColors.includes(color)
  const button = (
    <MuiButton
      classes={{ label: classes.label }}
      className={clsx([
        extendedColor && classes[`${variant}${pascalCase(color)}`],
        classes.root,
        className,
      ])}
      color={extendedColor ? undefined : color}
      disabled={isLoading}
      variant={variant}
      {...props}
    >
      {children}
    </MuiButton>
  )

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        {button}
        {isLoading && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
      </div>
    )
  } else {
    return button
  }
}

Button.propTypes = {
  color: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    ...extendedColors,
  ]),
  isLoading: PropTypes.bool,
}

Button.defaultProps = {
  disableElevation: true,
  isLoading: false,
  variant: 'contained',
}

export default Button
